import { useTranslation } from 'react-i18next';
import { useWebBookingBranding } from '../context/WebBookingBrandingContext';
import {
  resolveWebPublicExperienceForLocale,
  type WebPublicExperienceV1,
} from '../types/webPublicExperience';
import './WebPaymentFooter.css';

function hasFooterContent(exp: WebPublicExperienceV1): boolean {
  const { location, contact, trust, social, behavior } = exp;
  if (behavior.footerNote?.trim()) return true;
  if (trust.privacyPolicyUrl || trust.termsUrl || trust.cancellationNotice?.trim())
    return true;
  if (
    location.venueName ||
    location.addressLine ||
    location.city ||
    location.mapsUrl
  )
    return true;
  if (
    contact.supportPhone ||
    contact.supportWhatsapp ||
    contact.supportEmail ||
    contact.websiteUrl
  )
    return true;
  if (social.instagramUrl || social.facebookUrl) return true;
  return false;
}

function formatAddress(exp: WebPublicExperienceV1): string | null {
  const { venueName, addressLine, city, postalCode, region, countryCode } =
    exp.location;
  const lines: string[] = [];
  if (venueName?.trim()) lines.push(venueName.trim());
  if (addressLine?.trim()) lines.push(addressLine.trim());
  const cityLine = [postalCode, city].filter(Boolean).join(' ').trim();
  const tail = [cityLine, region, countryCode].filter(Boolean).join(', ').trim();
  if (tail) lines.push(tail);
  return lines.length ? lines.join('\n') : null;
}

export function WebPaymentFooter() {
  const { i18n, t } = useTranslation();
  const { webPublicExperience } = useWebBookingBranding();
  const exp = resolveWebPublicExperienceForLocale(
    webPublicExperience,
    i18n.language,
  );

  const address = formatAddress(exp);
  const { contact, trust, social, behavior } = exp;
  const hasCustomContent = hasFooterContent(exp);

  return (
    <footer className="wp-footer" role="contentinfo">
      <div className="wp-footer__inner">
        {hasCustomContent && address ? (
          <div className="wp-footer__block">
            <p className="wp-footer__title">{t('webUx.footer.whereWeAre')}</p>
            <p className="wp-footer__address">{address}</p>
            {exp.location.mapsUrl ? (
              <a
                href={exp.location.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('webUx.footer.openMap')}
              </a>
            ) : null}
          </div>
        ) : null}

        {hasCustomContent &&
          (contact.supportPhone ||
          contact.supportWhatsapp ||
          contact.supportEmail ||
          contact.websiteUrl) && (
          <div className="wp-footer__block">
            <p className="wp-footer__title">{t('webUx.footer.contact')}</p>
            <div className="wp-footer__links">
              {contact.supportPhone ? (
                <a href={`tel:${contact.supportPhone.replace(/\s/g, '')}`}>
                  {contact.supportPhone}
                </a>
              ) : null}
              {contact.supportEmail ? (
                <a href={`mailto:${contact.supportEmail}`}>
                  {contact.supportEmail}
                </a>
              ) : null}
              {contact.supportWhatsapp ? (
                <a
                  href={
                    contact.supportWhatsapp.startsWith('http')
                      ? contact.supportWhatsapp
                      : `https://wa.me/${contact.supportWhatsapp.replace(/^\+/, '')}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  WhatsApp
                </a>
              ) : null}
              {contact.websiteUrl ? (
                <a
                  href={contact.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('webUx.footer.website')}
                </a>
              ) : null}
            </div>
          </div>
        )}

        {hasCustomContent && (social.instagramUrl || social.facebookUrl) && (
          <div className="wp-footer__block">
            <p className="wp-footer__title">{t('webUx.footer.social')}</p>
            <div className="wp-footer__links">
              {social.instagramUrl ? (
                <a
                  href={social.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Instagram
                </a>
              ) : null}
              {social.facebookUrl ? (
                <a
                  href={social.facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Facebook
                </a>
              ) : null}
            </div>
          </div>
        )}

        {hasCustomContent &&
          (trust.privacyPolicyUrl ||
          trust.termsUrl ||
          trust.cancellationNotice?.trim()) && (
          <div className="wp-footer__block">
            <p className="wp-footer__title">{t('webUx.footer.legal')}</p>
            <div className="wp-footer__links">
              {trust.privacyPolicyUrl ? (
                <a
                  href={trust.privacyPolicyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('webUx.footer.privacy')}
                </a>
              ) : null}
              {trust.termsUrl ? (
                <a
                  href={trust.termsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('webUx.footer.terms')}
                </a>
              ) : null}
            </div>
            {trust.cancellationNotice?.trim() ? (
              <p className="wp-footer__note">{trust.cancellationNotice.trim()}</p>
            ) : null}
          </div>
        )}

        {hasCustomContent && behavior.footerNote?.trim() ? (
          <p className="wp-footer__note">{behavior.footerNote.trim()}</p>
        ) : null}

        <div className="wp-footer__legal-links">
          <a href="/aviso-legal">Aviso legal</a>
          <a href="/politica-de-privacidad">Política de privacidad</a>
          <a href="/politica-de-cookies">Política de cookies</a>
        </div>
        <div className="wp-footer__brand-note">
          <img className="wp-footer__logo" src="/tinoo-portal-logo.png" alt="Tinoo" />
          <p>Powered by Tinoo, a brand of Digital Innstinct S.L.</p>
        </div>
      </div>
    </footer>
  );
}
